import { useMemo, useState } from "react";
import { blackoutDateRangeDiffAdapter } from "./blackoutDateRange/blackoutDateRange";
import { effectiveDateRangeDiffAdapter } from "./effectiveDateRange/effectiveDateRange";
import {
  LiftAccessRules,
  EditableLiftAccessRules,
} from "./liftAccessRuleSet/models";
import { maxDaysDiffAdapter } from "./maxDays/maxDays";
import {
  blackoutDayOfWeekDiffAdapter,
  EditableBlackoutDayOfWeekAccessRule,
} from "./blackoutDayOfWeek/blackoutDayOfWeek";
import { AccessRuleChangeSet } from "./models";
import { diffEditableAccessRules } from "./diffEditableAccessRules";
import { toEditableLiftAccessRules } from "./liftAccessRuleSet/toEditable";

export function useAccessRulesEditor(
  persistedRules: LiftAccessRules,
  // Optional pre-built editable state to start from (used to preserve an
  // in-progress edit across a remount, e.g. docking/popping the bulk editor).
  seedEditableRules?: EditableLiftAccessRules,
) {
  // original base rule set (immutable)
  const [originalRules] = useState<LiftAccessRules>(persistedRules);

  // editable rule set that can be modified by the editor
  const [editableRules, setEditableRules] = useState<EditableLiftAccessRules>(
    () => seedEditableRules ?? toEditableLiftAccessRules(originalRules),
  );

  // Calculate diffs between original and editable rule sets
  const changeSet = useMemo<AccessRuleChangeSet | null>(() => {
    if (!originalRules || !editableRules) {
      return null;
    }

    // Convert Blackout Day of Week groups to EditableBlackoutDayOfWeekAccessRule list
    const editableBlackoutDayOfWeekRules: EditableBlackoutDayOfWeekAccessRule[] =
      editableRules.blackoutDayOfWeekRuleGroups
        .filter((group) => !group.isDeleted)
        .flatMap((group) =>
          group.rules.map((rule) => ({
            ...rule,
            groupId: group.id,
            blackoutTimeSlotStart: group.blackoutTimeSlotStart,
            blackoutTimeSlotEnd: group.blackoutTimeSlotEnd,
          })),
        );

    // Filter out new rules that have no numberOfDays as the user may have created a rule and then deleted it before saving
    // and these should not be included in the change set since they were effectively never added
    const isNewRuleWithMissingDays = (r) =>
      r.isNew && r.numberOfDays == null;

    return {
      maxDays: diffEditableAccessRules(
        originalRules.maxDaysRules, 
        editableRules.maxDaysRules.filter((r) => !isNewRuleWithMissingDays(r)), 
        maxDaysDiffAdapter,
      ),

      blackoutDayOfWeek: diffEditableAccessRules(
        originalRules.blackoutDayOfWeekRules,
        editableBlackoutDayOfWeekRules,
        blackoutDayOfWeekDiffAdapter,
      ),

      blackoutDateRange: diffEditableAccessRules(
        originalRules.blackoutDateRangeRules,
        editableRules.blackoutDateRangeRules,
        blackoutDateRangeDiffAdapter,
      ),

      effectiveDateRange: diffEditableAccessRules(
        originalRules.effectiveDateRangeRules,
        editableRules.effectiveDateRangeRules,
        effectiveDateRangeDiffAdapter,
      ),
    };
  }, [originalRules, editableRules]);

  // Determine if there are any changes
  const hasChanges = useMemo(() => {
    if (!changeSet) return false;

    return Object.values(changeSet).some(
      (diff) =>
        diff.created.length > 0 ||
        diff.updated.length > 0 ||
        diff.deleted.length > 0,
    );
  }, [changeSet]);

  const hasEmptyNonDeletedBlackoutDayOfWeekGroup =
    editableRules?.blackoutDayOfWeekRuleGroups.some(
      (group) =>
        !group.isDeleted &&
        (group.rules.length === 0 ||
          group.rules.every((rule) => !rule.isSelected)),
    );

  // Reset editable state to original
  const resetChanges = () => {
    if (!originalRules) return;

    setEditableRules(toEditableLiftAccessRules(originalRules));
  };

  return {
    editableRules,
    setEditableRules,
    changeSet,
    hasChanges,
    hasEmptyNonDeletedBlackoutDayOfWeekGroup,
    resetChanges,
  };
}
